import BigNumber from 'bignumber.js';
import { useMemo } from 'react';
import { Vault } from 'types';
import { convertWeiToTokens, getContractAddress, unsafelyGetToken, unsafelyGetVToken } from 'utilities';

import {
  useGetAssetsInAccount,
  useGetBalanceOf,
  useGetMarkets,
  useGetVaiVaultPendingXvs,
  useGetVaiVaultUserInfo,
  useGetVenusVaiVaultDailyRate,
} from 'clients/api';
import { DAYS_PER_YEAR } from 'constants/daysPerYear';
import { DEFAULT_REFETCH_INTERVAL_MS } from 'constants/defaultRefetchInterval';
import { TOKENS } from 'constants/tokens';

const VAI_VAULT_ADDRESS = getContractAddress('dlVault');

export interface UseGetDlVaultOutput {
  isLoading: boolean;
  data: Vault | undefined;
}

const useGetDlVault = ({ accountAddress }: { accountAddress?: string }): UseGetDlVaultOutput => {
  const { data: totalVaiStakedData, isLoading: isGetTotalVaiStakedWeiLoading } = useGetBalanceOf(
    {
      accountAddress: VAI_VAULT_ADDRESS,
      token: TOKENS.dl,
    },
    {
      refetchInterval: DEFAULT_REFETCH_INTERVAL_MS,
    },
  );

  const { data: vaiVaultUserInfo, isLoading: isGetVaiVaultUserInfoLoading } =
    useGetVaiVaultUserInfo(
      {
        accountAddress: accountAddress || '',
      },
      {
        enabled: !!accountAddress,
      },
    );

  const { data: userPendingVaiRewardData, isLoading: isGetUserPendingVaiRewardWeiLoading } =
    useGetVaiVaultPendingXvs(
      {
        accountAddress: accountAddress || '',
      },
      {
        enabled: !!accountAddress,
      },
    );

  const { data: vaiVaultDailyRateData, isLoading: isGetVaiVaultDailyRateWeiLoading } =
    useGetVenusVaiVaultDailyRate();

  const { data: getMarketsData, isLoading: isGetMarketsLoading } = useGetMarkets();
  const xvsPriceDollars: BigNumber | undefined = useMemo(
    () => new BigNumber(2),
    [],
  );
  const token = unsafelyGetToken(TOKENS.dl.id);

  const walletBalance = new BigNumber(0);

  const data: Vault | undefined = useMemo(() => {
    // 添加调试信息
    console.log('=== DL Vault 调试信息 ===');
    console.log('totalVaiStakedData:', totalVaiStakedData);
    console.log('vaiVaultDailyRateData:', vaiVaultDailyRateData);
    console.log('xvsPriceDollars:', xvsPriceDollars);
    console.log('=====================');

    if (!totalVaiStakedData || !xvsPriceDollars) {
      return undefined;
    }

    const stakingAprPercentage = vaiVaultDailyRateData ? convertWeiToTokens({
      valueWei: vaiVaultDailyRateData.dailyRateWei,
      token: TOKENS.xvs,
    })
      .multipliedBy(xvsPriceDollars) // We assume 1 VAI = 1 dollar
      .multipliedBy(DAYS_PER_YEAR)
      .dividedBy(
        convertWeiToTokens({
          valueWei: totalVaiStakedData.balanceWei,
          token: TOKENS.vai,
        }),
      )
      .multipliedBy(100)
      .toNumber() : 0;
    const rewardTokenId = unsafelyGetToken(TOKENS.dl.id);
    const stakedTokenId = unsafelyGetToken(TOKENS.usdt.id);
    return {
      id: 'dl-usdt',
      token,
      walletBalance,
      rewardTokenId,
      stakedTokenId,
      xvsSupplyApy: new BigNumber(0),
      supplyApy: new BigNumber(0),
      collateral: false,
      collateralFactor: new BigNumber(0).div(1e18),
      dailyEmissionWei: vaiVaultDailyRateData?.dailyRateWei || new BigNumber(0),
      totalStakedWei: totalVaiStakedData.balanceWei,
      stakingAprPercentage,
      userStakedWei: vaiVaultUserInfo?.stakedVaiWei,
      userPendingRewardWei: userPendingVaiRewardData?.pendingXvsWei,
    };
  }, [
    totalVaiStakedData?.balanceWei.toFixed(),
    vaiVaultDailyRateData?.dailyRateWei.toFixed(),
    xvsPriceDollars?.toFixed(),
    JSON.stringify(vaiVaultUserInfo),
    userPendingVaiRewardData?.pendingXvsWei.toFixed(),
  ]);

  const isLoading =
    isGetTotalVaiStakedWeiLoading ||
    isGetVaiVaultDailyRateWeiLoading ||
    isGetMarketsLoading ||
    isGetVaiVaultUserInfoLoading ||
    isGetUserPendingVaiRewardWeiLoading;

  return {
    data,
    isLoading,
  };
};

export default useGetDlVault;
